/**
 * XML 结构自检（生成后，针对已序列化的 ReportIR）。
 * 校验：坐标唯一性、合并越界、父格成环、数据集字段引用有效性。
 * 返回可定位到具体单元格的错误列表（不抛异常，供 CLI 汇总输出）。
 */

import type { ReportIR, ParameterIR } from '../ir/types.js';
import { expandGrid, GridError } from '../grid/expand.js';
import { detectParentCycle, normalizeTopParentCells } from '../grid/parent.js';
import { coordName } from '../grid/coordinate.js';
import { extractParamPlaceholders, isParamName } from '../ir/types.js';

/** editorType 别名 → 官方标准值（与 serialize/parse 的归一逻辑保持一致）。 */
const EDITOR_TYPE_ALIAS: Record<string, string> = {
  textarea: 'text',
  input: 'text',
  dropdown: 'select',
  combo: 'select',
  combobox: 'select',
  datepicker: 'date',
  calendar: 'date',
};

export interface StructureIssue {
  level: 'error' | 'warn';
  cell?: string; // A1 风格坐标
  message: string;
}

/**
 * 简单 Levenshtein 编辑距离，用于参数名拼写错误的「你是不是想写 X」建议。
 */
function editDistance(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array<number>(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i]![0] = i;
  for (let j = 0; j <= n; j++) dp[0]![j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i]![j] = Math.min(dp[i - 1]![j]! + 1, dp[i]![j - 1]! + 1, dp[i - 1]![j - 1]! + cost);
    }
  }
  return dp[m]![n]!;
}

/** 从候选参数名中找与 target 最相近（编辑距离最小且 ≤2）的一个，供修正建议。 */
function suggestParamName(target: string, candidates: string[]): string | undefined {
  let best: string | undefined;
  let bestDist = Infinity;
  for (const c of candidates) {
    const d = editDistance(target.toLowerCase(), c.toLowerCase());
    if (d < bestDist) {
      bestDist = d;
      best = c;
    }
  }
  return bestDist <= 2 ? best : undefined;
}

export function structureCheck(report: ReportIR): StructureIssue[] {
  const issues: StructureIssue[] = [];

  // 1. 网格展开（合并越界/重叠/重复由 expandGrid 抛错，这里捕获为 issue）
  let grid;
  try {
    grid = expandGrid(report);
  } catch (e) {
    if (e instanceof GridError) {
      issues.push({ level: 'error', message: e.message });
      return issues;
    }
    throw e;
  }

  // 2. 父格成环（先规范化顶层父格码，再检测左/上父格交叉环）
  const cycle = detectParentCycle(normalizeTopParentCells(report.cells));
  if (cycle) issues.push({ level: 'error', message: cycle });

  // 3. 数据集字段引用有效性
  const datasets = new Map((report.datasets ?? []).map((d) => [d.name, d]));
  for (const c of report.cells) {
    const ref = c.content;
    if (ref?.kind === 'dataset') {
      const ds = datasets.get(ref.dataset);
      const where = `${coordName(c.row, c.col)} (${ref.dataset}.${ref.field})`;
      if (!ds) {
        issues.push({ level: 'error', cell: coordName(c.row, c.col), message: `${where}: 引用的数据集不存在` });
        continue;
      }
      if (!ds.fields.some((f) => f.name === ref.field)) {
        issues.push({
          level: 'error',
          cell: coordName(c.row, c.col),
          message: `${where}: 字段在数据集 ${ref.dataset} 中未定义`,
        });
      }
    }
  }

  // 3b. 单元格样式/聚合枚举合法性（对应官方 FormatType.valueOf / AggregateType.valueOf
  //     无空保护，非法值会在引擎端 NPE。提前在这里拦截，避免生成无法解析的 XML）。
  const FORMAT_TYPES = new Set(['text', 'date', 'currency', 'number', 'custom']);
  const AGG_TYPES = new Set([
    // IR 层 'none' 表示「不聚合（明细）」，serialize 会翻译为官方 'select'，故此处合法。
    'none', 'group', 'conditiongroup', 'expressiongroup', 'select', 'sum', 'avg',
    'max', 'min', 'count', 'countd', 'percentile', 'variance', 'std',
  ]);
  for (const c of report.cells) {
    const where = coordName(c.row, c.col);
    const fmt = c.style?.formatType;
    if (fmt && !FORMAT_TYPES.has(fmt)) {
      issues.push({
        level: 'error',
        cell: where,
        message: `${where}.style.formatType=${fmt}: 非法格式化类型（官方 FormatType 仅支持 ${[...FORMAT_TYPES].join('/')}）`,
      });
    }
    const agg = c.content?.kind === 'dataset' ? c.content.aggregate : undefined;
    if (agg && !AGG_TYPES.has(agg)) {
      issues.push({
        level: 'error',
        cell: where,
        message: `${where}.content.aggregate=${agg}: 非法聚合类型（官方 AggregateType 不含 'none' 或任意未识别值，缺省请用 'select'）`,
      });
    }
  }

  // 3b-2. 比较运算符合法性（对齐 methodology/filter/filter.md 比较符全集：
  //       表达式符号 ==/!=/>=/<=/>/< 与过滤器单词形式 contains/notContains/
  //       startsWith/notStartsWith/endsWith/notEndsWith/in/notIn。
  //       非法 op 会在引擎端解析失败，提前拦截并给出完整候选集）。
  const OPS = new Set([
    '==', '!=', '>', '>=', '<', '<=',
    'contains', 'notContains', 'startsWith', 'notStartsWith',
    'endsWith', 'notEndsWith', 'in', 'notIn',
  ]);
  const checkOp = (loc: string, op?: string) => {
    if (op && !OPS.has(op)) {
      issues.push({
        level: 'error',
        cell: loc,
        message: `${loc}.op=${op}: 非法比较运算符（支持 ${[...OPS].join(' ')}）`,
      });
    }
  };
  for (const c of report.cells) {
    const where = coordName(c.row, c.col);
    if (c.content?.kind === 'dataset') {
      for (const cond of c.content.conditions ?? []) {
        checkOp(`${where}.content.conditions.${cond.left}`, cond.op);
      }
    }
    for (const r of c.style?.render ?? []) {
      checkOp(`${where}.style.render`, r.op);
    }
  }

  // 3c. FieldType 仅支持 string/number/date/boolean，`datetime` 非法（引擎端无此类型、会被静默忽略或报错）。
  //     常见误写是把日期时间字段写成 datetime，正确应为 date。这里提前拦截并给出「是不是想写 date」建议。
  const FIELD_TYPES = new Set(['string', 'number', 'date', 'boolean']);
  const checkFieldType = (loc: string, t?: string) => {
    if (!t) return;
    if (t === 'datetime') {
      issues.push({
        level: 'error',
        cell: loc,
        message: `${loc}.type="datetime": 非法字段类型（FieldType 仅支持 string/number/date/boolean）。日期时间字段请改用 "date"。`,
      });
    } else if (!FIELD_TYPES.has(t)) {
      issues.push({
        level: 'error',
        cell: loc,
        message: `${loc}.type="${t}": 非法字段类型（FieldType 仅支持 string/number/date/boolean）。`,
      });
    }
  };
  for (const ds of report.datasets ?? []) {
    for (const f of ds.fields ?? []) checkFieldType(`datasets.${ds.name}.fields.${f.name}`, f.type);
  }
  for (const p of report.parameters ?? []) checkFieldType(`parameters.${p.name}`, p.type);

  // 4. 参数占位符拼写校验（仅当报表声明了 parameters 或数据集 SQL 含占位符）
  const paramDefs = report.parameters ?? [];
  const paramNames = paramDefs.map((p) => p.name);
  const declaredParamSet = new Set(paramNames.map((n) => n.replace(/^\$/, '')));

  // 4a. 参数名必须合法（参数名本身不含 `$`，`$` 仅属于占位符语法）
  for (const p of paramDefs) {
    if (!isParamName(p.name)) {
      issues.push({
        level: 'error',
        message: `参数「${p.name}」命名不规范：参数名本身不应带前导 \`$\`（如 \`dept\`），SQL 中对应占位符才写为 \`\${$dept}\`。`,
      });
    }
  }

  // 4b. 收集所有数据集 SQL 中实际引用的占位符，与 parameters 比对
  const usedBare = new Set<string>();
  for (const ds of report.datasets ?? []) {
    if (ds.type !== 'sql' || !ds.sql) continue;
    for (const bare of extractParamPlaceholders(ds.sql)) {
      usedBare.add(bare);
      if (!declaredParamSet.has(bare)) {
        const suggest = suggestParamName(bare, paramNames);
        const hint = suggest ? ` 你是不是想写 \`\${$${suggest}}\`？` : '';
        issues.push({
          level: 'error',
          message: `数据集「${ds.name}」的 SQL 引用了未定义的参数占位符 \`\${$${bare}}\`：${hint}请在 parameters 中声明 \`{ "name": "${bare}" }\`。`,
        });
      }
    }
  }

  // 4c. parameters 中声明但 SQL 未使用的参数 → warn（可能是拼写不一致）
  for (const p of paramDefs) {
    const bare = p.name.replace(/^\$/, '');
    if (!usedBare.has(bare)) {
      issues.push({
        level: 'warn',
        message: `参数「${p.name}」已在 parameters 声明，但没有数据集 SQL 引用它（占位符 \`\${$${bare}}\` 未出现在任何 SQL 中），请确认是否拼写不一致或多余。`,
      });
    }
  }

  // 4d. editorType 合法性 + select 必须配 binding（覆盖 parameterForm 与 parameters[] 两条路径）。
  // 合法值含官方标准集及常用别名（serialize 会归一为 text/select/date/button）。
  const KNOWN_EDITOR_TYPES = new Set([
    'text', 'textarea', 'input',
    'select', 'dropdown', 'combo', 'combobox',
    'date', 'datepicker', 'calendar',
    'button',
  ]);
  const collectEditorUsages = () => {
    const usages: Array<{ src: string; editorType?: string; binding?: ParameterIR['binding'] }> = [];
    for (const fe of report.parameterForm ?? []) {
      usages.push({ src: `parameterForm.${fe.name}`, editorType: fe.editorType, binding: fe.binding });
    }
    for (const p of paramDefs) {
      usages.push({ src: `parameters.${p.name}`, editorType: p.editorType, binding: p.binding });
    }
    return usages;
  };
  for (const u of collectEditorUsages()) {
    const et = (u.editorType ?? 'text').toLowerCase();
    if (!KNOWN_EDITOR_TYPES.has(et)) {
      issues.push({
        level: 'error',
        message: `${u.src}.editorType="${u.editorType}"：非法编辑器类型（支持 text/select/date/button 及其别名 textarea/dropdown/datepicker 等）。`,
      });
      continue;
    }
    const norm = EDITOR_TYPE_ALIAS[et] ?? et;
    if (norm === 'select' && (!u.binding || (u.binding.customData ?? []).length === 0)) {
      issues.push({
        level: 'error',
        message: `${u.src}.editorType="select"：下拉控件必须配置 binding.customData 提供可选项。`,
      });
    }
  }

  // 5. 单元格坐标唯一性（name 与 row/col 一致性）
  const seen = new Map<string, string>();
  for (const g of grid.cells) {
    const name = g.cell.name!;
    const expected = coordName(g.cell.row, g.cell.col);
    if (g.cell.name && g.cell.name !== expected && g.cell.rowspan === 1 && g.cell.colspan === 1) {
      // 允许显式指定 name，但需与坐标一致才能被引擎识别
      issues.push({
        level: 'warn',
        cell: name,
        message: `单元格 name=${name} 与坐标 (${g.cell.row},${g.cell.col}) 推导的 ${expected} 不一致`,
      });
    }
    if (seen.has(name)) {
      issues.push({ level: 'error', cell: name, message: `坐标 ${name} 重复定义` });
    }
    seen.set(name, name);
  }

  // 5. 合并覆盖格不应携带内容（被覆盖格仍可能被给出 content，提示）
  // （expandGrid 已确保被覆盖格不输出；此处不再重复）

  return issues;
}
